import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';
import { useGetShippingsQuery } from '../services/shippings.service';
import { Option } from '@/components/inputs/AutocompleteInput';
import type { AddressApi } from '../models';

interface UseDestinationAddressAutocompleteProps {
  search?: string;
  minLength?: number;
  enabled?: boolean;
}

function destinationDeliveryAddressLine(
  address: AddressApi | null | undefined
): string | null {
  if (!address) return null;
  const parts = [
    address.streetName,
    address.doorNumber,
    address.apartmentNumber,
  ].filter(Boolean);
  const line = parts.join(' ').trim();
  return line.length > 0 ? line : null;
}

const useDestinationAddressAutocomplete = ({
  search,
  minLength = 3,
  enabled = true,
}: UseDestinationAddressAutocompleteProps = {}) => {
  const {
    debouncedSearch: destinationAddressSearchTextTrimmed,
    shouldSearch: debouncedShouldFetchDestinationAddressSuggestions,
    isDebouncePending,
  } = useAutocompleteSearch(search, minLength);
  const shouldFetchDestinationAddressSuggestions = enabled && debouncedShouldFetchDestinationAddressSuggestions;

  const {
    data: shippingsResponse,
    isFetching: isDestinationAddressFetching,
    error: destinationAddressError,
  } = useGetShippingsQuery(
    {
      paginated: { page: 1, pageSize: 10 },
      filters: {
        destinationAddress: shouldFetchDestinationAddressSuggestions
          ? destinationAddressSearchTextTrimmed
          : '',
      },
    },
    { skip: !shouldFetchDestinationAddressSuggestions }
  );

  const resolvedDestinationAddressOptions: Option[] = useMemo(() => {
    if (!shippingsResponse?.items) return [];

    const addressLineByCaseInsensitiveKey = new Map<string, string>();

    for (const shipping of shippingsResponse.items) {
      const addressLine = destinationDeliveryAddressLine(
        shipping.destinationAddress
      );
      if (!addressLine) continue;

      const caseInsensitiveAddressKey = addressLine.toLocaleLowerCase('es');

      if (!addressLineByCaseInsensitiveKey.has(caseInsensitiveAddressKey)) {
        addressLineByCaseInsensitiveKey.set(
          caseInsensitiveAddressKey,
          addressLine
        );
      }
    }

    const uniqueAddressLines = [...addressLineByCaseInsensitiveKey.values()];

    uniqueAddressLines.sort(
      (firstComparedAddressLine, secondComparedAddressLine) =>
        firstComparedAddressLine.localeCompare(
          secondComparedAddressLine,
          'es',
          { sensitivity: 'base' }
        )
    );

    return uniqueAddressLines.map((addressLine) => ({
      label: addressLine,
      data: addressLine,
    }));
  }, [shippingsResponse?.items]);

  const destinationAddressOptions = useStableOptions(resolvedDestinationAddressOptions, {
    isPending: isDestinationAddressFetching || isDebouncePending,
    enabled: shouldFetchDestinationAddressSuggestions || (enabled && isDebouncePending),
  });

  const isDestinationAddressLoading = shouldFetchDestinationAddressSuggestions && isDestinationAddressFetching;

  return {
    destinationAddressOptions,
    isDestinationAddressLoading,
    destinationAddressError,
  };
};

export default useDestinationAddressAutocomplete;
